<template>
  <div class="am-root">
    <div class="am-topbar">
      <div class="am-tabs">
        <button class="am-tab" :class="{ 'am-tab--active': activeTab === 'estrutura' }" @click="activeTab = 'estrutura'">Estrutura</button>
        <button class="am-tab" :class="{ 'am-tab--active': activeTab === 'alunos' }" @click="switchToAlunos">Alunos</button>
      </div>
      <div class="am-topbar__right">
        <button class="am-btn am-btn--ghost" @click="copyStudentLink">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" style="margin-right:5px"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
          {{ linkCopied ? 'Link copiado!' : 'Link do aluno' }}
        </button>
        <button class="am-btn am-btn--ghost" @click="previewCourse">Pré-visualizar</button>
        <button v-if="activeTab === 'estrutura'" class="am-btn am-btn--primary" :disabled="isSavingModule" @click="saveModule">
          {{ isSavingModule ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </div>

    <div v-if="actionError" class="am-alert--error">{{ actionError }}</div>

    <!-- ── Monitoring tab ── -->
    <div v-if="activeTab === 'alunos'" class="am-monitoring">
      <div class="am-monitoring__header">
        <h3 class="am-monitoring__title">Progresso dos alunos</h3>
        <button class="am-btn am-btn--ghost am-btn--sm" @click="loadMonitoring">↺ Atualizar</button>
      </div>

      <div v-if="monitoringLoading" class="am-empty am-empty--pad">Carregando...</div>
      <div v-else-if="!monitoring.students || !monitoring.students.length" class="am-empty am-empty--pad">
        Nenhum aluno acessou o treinamento ainda.
      </div>
      <template v-else>
        <!-- Students table -->
        <div class="am-mon-table-wrap">
          <table class="am-mon-table">
            <thead>
              <tr>
                <th>Aluno</th>
                <th>Aulas acessadas</th>
                <th>Concluídas</th>
                <th>% médio</th>
                <th>Minutos assistidos</th>
                <th>Último acesso</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in monitoring.students" :key="s.student_key">
                <td>
                  <div class="am-mon-student">
                    <span class="am-mon-student__name">{{ s.nome || s.student_key }}</span>
                    <span v-if="s.email" class="am-mon-student__email">{{ s.email }}</span>
                  </div>
                </td>
                <td>{{ s.lessons_accessed }}</td>
                <td>
                  <span class="am-mon-badge" :class="s.lessons_completed >= monitoring.total_lessons ? 'am-mon-badge--done' : ''">
                    {{ s.lessons_completed }} / {{ monitoring.total_lessons }}
                  </span>
                </td>
                <td>
                  <div class="am-mon-pct-wrap">
                    <div class="am-mon-pct-bar"><div class="am-mon-pct-fill" :style="{ width: s.avg_progress + '%' }"></div></div>
                    <span>{{ s.avg_progress }}%</span>
                  </div>
                </td>
                <td>{{ s.total_watched_min }}min</td>
                <td class="am-mon-date">{{ formatDate(s.last_seen_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Ratings per lesson -->
        <div v-if="monitoring.lesson_ratings && monitoring.lesson_ratings.some(r => r.total_ratings > 0)" class="am-mon-ratings">
          <h4 class="am-mon-ratings__title">Avaliações por aula</h4>
          <div class="am-mon-ratings__list">
            <div
              v-for="r in monitoring.lesson_ratings.filter(r => r.total_ratings > 0)"
              :key="r.lesson_id"
              class="am-mon-rating-row"
            >
              <div class="am-mon-rating-info">
                <span class="am-mon-rating-module">{{ r.module_title }}</span>
                <span class="am-mon-rating-lesson">{{ r.lesson_title }}</span>
              </div>
              <div class="am-mon-stars">
                <span
                  v-for="s in 5" :key="s"
                  class="am-mon-star"
                  :class="{ 'am-mon-star--on': s <= Math.round(r.avg_rating || 0) }"
                >★</span>
                <span class="am-mon-star-avg">{{ r.avg_rating || 0 }} ({{ r.total_ratings }})</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-show="activeTab === 'estrutura'" class="am-panels">
      <!-- ── Left panel: module list ── -->
      <div class="am-left">
        <div class="am-left__header">
          <span class="am-left__title">Módulos</span>
          <span class="am-left__count">{{ modules.length }}</span>
        </div>

        <div v-if="isLoadingModules" class="am-empty am-empty--pad">Carregando...</div>
        <div v-else-if="!modules.length" class="am-empty am-empty--pad">Nenhum módulo. Crie o primeiro abaixo.</div>

        <div class="am-module-list">
          <div
            v-for="(m, idx) in modules"
            :key="m.id"
            class="am-module-item"
            :class="{ 'am-module-item--active': selectedModuleId === m.id }"
            draggable="true"
            @dragstart="onModuleDragStart($event, idx)"
            @dragover.prevent="onModuleDragOver($event, idx)"
            @dragend="onModuleDragEnd"
            @click="selectModule(m.id)"
          >
            <span class="am-drag-handle">⋮⋮</span>
            <div class="am-module-item__body">
              <div class="am-module-item__title">
                <span class="am-module-item__num">{{ idx + 1 }}.</span>
                {{ m.title }}
              </div>
              <div class="am-module-item__meta">
                {{ m.lessons ? m.lessons.length : 0 }} aulas
                <template v-if="moduleTotalDuration(m)"> · {{ moduleTotalDuration(m) }}</template>
              </div>
            </div>
            <span class="am-badge" :class="statusClass(m.status)">{{ statusLabel(m.status) }}</span>
          </div>
        </div>

        <button class="am-btn-add-module" @click="createModule">+ Novo módulo</button>

        <!-- Student access link -->
        <div class="am-student-link">
          <div class="am-student-link__header">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
            </svg>
            <span>Link do aluno</span>
          </div>
          <p class="am-student-link__desc">
            Substitua <code>{CHAVE}</code> pelo ID do lead antes de enviar.
          </p>
          <div class="am-student-link__row">
            <span class="am-student-link__url">{{ studentLinkTemplate }}</span>
            <button class="am-student-link__copy" @click="copyStudentLink" :title="linkCopied ? 'Copiado!' : 'Copiar link'">
              <svg v-if="!linkCopied" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
              </svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#28C76F" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </button>
          </div>
          <button class="am-student-link__preview" @click="previewCourse">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
            Pré-visualizar como aluno
          </button>
        </div>
      </div>

      <!-- ── Right panel: module form + lessons ── -->
      <div class="am-right">
        <div v-if="!selectedModule" class="am-empty am-empty--center">
          Selecione um módulo na lista para editar.
        </div>

        <template v-else>
          <!-- Module form -->
          <div class="am-section">
            <h3 class="am-section__title">Dados do módulo</h3>
            <div class="am-form-grid">
              <div class="am-field am-field--flex3">
                <label class="am-label">Título</label>
                <input v-model="moduleEditForm.title" class="am-input" placeholder="Título do módulo">
              </div>
              <div class="am-field am-field--flex1">
                <label class="am-label">Ordem</label>
                <select v-model.number="moduleEditForm.sort_order" class="am-input">
                  <option v-for="n in modules.length" :key="n" :value="n - 1">{{ n }}</option>
                </select>
              </div>
              <div class="am-field am-field--flex1">
                <label class="am-label">Status</label>
                <select v-model="moduleEditForm.status" class="am-input">
                  <option value="draft">Rascunho</option>
                  <option value="editing">Editando</option>
                  <option value="published">Publicado</option>
                </select>
              </div>
            </div>
            <div class="am-field">
              <label class="am-label">Descrição</label>
              <textarea v-model="moduleEditForm.description" class="am-textarea" rows="2" placeholder="Descrição opcional do módulo" />
            </div>
            <div class="am-form-actions">
              <button class="am-btn am-btn--danger-ghost" @click="confirmDeleteModule(selectedModule.id)">Excluir módulo</button>
              <button class="am-btn am-btn--primary" :disabled="isSavingModule" @click="saveModule">
                {{ isSavingModule ? 'Salvando...' : 'Salvar módulo' }}
              </button>
            </div>
          </div>

          <!-- Lessons section -->
          <div class="am-section">
            <h3 class="am-section__title">
              Aulas do módulo
              <span class="am-section__count">{{ selectedModule.lessons ? selectedModule.lessons.length : 0 }}</span>
            </h3>

            <div v-if="!selectedModule.lessons || !selectedModule.lessons.length" class="am-empty">
              Nenhuma aula neste módulo. Adicione abaixo.
            </div>

            <div>
              <div
                v-for="(lesson, lIdx) in selectedModule.lessons"
                :key="lesson.id"
                class="am-lesson-item"
                draggable="true"
                @dragstart="onLessonDragStart($event, lIdx)"
                @dragover.prevent="onLessonDragOver($event, lIdx)"
                @dragend="onLessonDragEnd"
              >
                <template v-if="lessonEditId === lesson.id">
                  <div class="am-lesson-edit-form">
                    <div class="am-form-grid">
                      <div class="am-field am-field--flex3">
                        <label class="am-label">Título</label>
                        <input v-model="lessonEditForm.title" class="am-input" placeholder="Título da aula">
                      </div>
                      <div class="am-field am-field--flex1">
                        <label class="am-label">Tipo</label>
                        <select v-model="lessonEditForm.lesson_type" class="am-input">
                          <option value="video">Vídeo</option>
                          <option value="text">Texto</option>
                          <option value="exercise">Exercício</option>
                        </select>
                      </div>
                    </div>
                    <div class="am-form-grid">
                      <div v-if="lessonEditForm.lesson_type === 'video'" class="am-field am-field--flex2">
                        <label class="am-label">VSL</label>
                        <div class="am-vsl-dd" @click.stop>
                          <button type="button" class="am-vsl-dd__trigger" @click="toggleVslDropdown('edit')">
                            <span class="am-vsl-dd__label">{{ vslNameById(lessonEditForm.id_vsl) }}</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                          </button>
                          <div v-if="vslDropdownOpen === 'edit'" class="am-vsl-dd__panel">
                            <div class="am-vsl-dd__search-wrap">
                              <input :ref="'vslSearch_edit'" v-model="vslSearch" class="am-vsl-dd__search" placeholder="Buscar vídeo..." />
                            </div>
                            <div class="am-vsl-dd__list">
                              <div class="am-vsl-dd__item" :class="{ 'am-vsl-dd__item--selected': !lessonEditForm.id_vsl }" @click="selectVsl('edit', '')">Selecione o vídeo</div>
                              <div v-for="v in filteredVsls" :key="v.id" class="am-vsl-dd__item" :class="{ 'am-vsl-dd__item--selected': String(lessonEditForm.id_vsl) === String(v.id) }" @click="selectVsl('edit', v.id)">{{ v.vsl_name }}</div>
                              <div v-if="filteredVsls.length === 0 && vslSearch" class="am-vsl-dd__empty">Nenhum resultado</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="am-field am-field--flex1">
                        <label class="am-label">Duração (min)</label>
                        <input v-model.number="lessonEditForm.duration_min" class="am-input" type="number" min="0" placeholder="Ex: 12">
                      </div>
                      <div class="am-field am-field--flex1">
                        <label class="am-label">Status</label>
                        <select v-model="lessonEditForm.status" class="am-input">
                          <option value="draft">Rascunho</option>
                          <option value="editing">Editando</option>
                          <option value="published">Publicado</option>
                        </select>
                      </div>
                    </div>
                    <div class="am-field">
                      <label class="am-label">Descrição</label>
                      <textarea v-model="lessonEditForm.description" class="am-textarea" rows="2" />
                    </div>
                    <div class="am-form-actions">
                      <button class="am-btn am-btn--ghost" @click="cancelLessonEdit">Cancelar</button>
                      <button class="am-btn am-btn--danger-ghost" @click="confirmDeleteLesson(lesson.id)">Excluir</button>
                      <button class="am-btn am-btn--primary" :disabled="isSavingLesson" @click="saveLesson(lesson)">
                        {{ isSavingLesson ? 'Salvando...' : 'Salvar aula' }}
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span class="am-drag-handle">⋮⋮</span>
                  <span class="am-lesson-type-icon" :class="lessonTypeIconClass(lesson.lesson_type)">
                    {{ lessonTypeIcon(lesson.lesson_type) }}
                  </span>
                  <div class="am-lesson-item__body">
                    <div class="am-lesson-item__title">
                      <span class="am-lesson-item__num">{{ moduleIndexLabel(lIdx) }}</span>
                      {{ lesson.title }}
                    </div>
                    <div class="am-lesson-item__meta">
                      {{ lessonTypeLabel(lesson.lesson_type) }}
                      <template v-if="lesson.duration_min"> · {{ lesson.duration_min }}min</template>
                       · {{ statusLabel(lesson.status) }}
                    </div>
                  </div>
                  <button class="am-btn am-btn--ghost am-btn--sm" @click.stop="startLessonEdit(lesson)">Editar</button>
                </template>
              </div>
            </div>

            <div v-if="addingLesson" class="am-add-lesson-form">
              <div class="am-form-grid">
                <div class="am-field am-field--flex3">
                  <label class="am-label">Título</label>
                  <input v-model="newLessonForm.title" class="am-input" placeholder="Título da aula">
                </div>
                <div class="am-field am-field--flex1">
                  <label class="am-label">Tipo</label>
                  <select v-model="newLessonForm.lesson_type" class="am-input">
                    <option value="video">Vídeo</option>
                    <option value="text">Texto</option>
                    <option value="exercise">Exercício</option>
                  </select>
                </div>
              </div>
              <div v-if="newLessonForm.lesson_type === 'video'" class="am-form-grid">
                <div class="am-field am-field--flex2">
                  <label class="am-label">VSL</label>
                  <div class="am-vsl-dd" @click.stop>
                    <button type="button" class="am-vsl-dd__trigger" @click="toggleVslDropdown('new')">
                      <span class="am-vsl-dd__label">{{ vslNameById(newLessonForm.id_vsl) }}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    <div v-if="vslDropdownOpen === 'new'" class="am-vsl-dd__panel">
                      <div class="am-vsl-dd__search-wrap">
                        <input :ref="'vslSearch_new'" v-model="vslSearch" class="am-vsl-dd__search" placeholder="Buscar vídeo..." />
                      </div>
                      <div class="am-vsl-dd__list">
                        <div class="am-vsl-dd__item" :class="{ 'am-vsl-dd__item--selected': !newLessonForm.id_vsl }" @click="selectVsl('new', '')">Selecione o vídeo</div>
                        <div v-for="v in filteredVsls" :key="v.id" class="am-vsl-dd__item" :class="{ 'am-vsl-dd__item--selected': String(newLessonForm.id_vsl) === String(v.id) }" @click="selectVsl('new', v.id)">{{ v.vsl_name }}</div>
                        <div v-if="filteredVsls.length === 0 && vslSearch" class="am-vsl-dd__empty">Nenhum resultado</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="am-field am-field--flex1">
                  <label class="am-label">Duração (min)</label>
                  <input v-model.number="newLessonForm.duration_min" class="am-input" type="number" min="0">
                </div>
              </div>
              <div class="am-form-actions">
                <button class="am-btn am-btn--ghost" @click="addingLesson = false">Cancelar</button>
                <button class="am-btn am-btn--primary" :disabled="isSavingLesson" @click="createLesson">
                  {{ isSavingLesson ? 'Adicionando...' : 'Adicionar aula' }}
                </button>
              </div>
            </div>
            <button v-else class="am-btn-add-lesson" @click="addingLesson = true">+ Adicionar aula</button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '@/composables/useApi'

const apiBack = useApi()

export default {
  name: 'AcademyManager',
  data() {
    return {
      modules: [],
      vsls: [],
      vslDropdownOpen: '',
      vslSearch: '',
      selectedModuleId: null,
      moduleEditForm: { title: '', description: '', sort_order: 0, status: 'draft' },
      lessonEditId: null,
      lessonEditForm: { title: '', description: '', id_vsl: '', lesson_type: 'video', duration_min: null, status: 'draft', sort_order: 0 },
      newLessonForm: { title: '', description: '', id_vsl: '', lesson_type: 'video', duration_min: null },
      addingLesson: false,
      isLoadingModules: false,
      isSavingModule: false,
      isSavingLesson: false,
      actionError: '',
      apiMode: 'unknown',
      moduleDragFromIdx: null,
      lessonDragFromIdx: null,
      activeTab: 'estrutura',
      monitoring: { students: [], total_lessons: 0, lesson_ratings: [] },
      monitoringLoading: false,
      linkCopied: false,
    }
  },
  computed: {
    userData() {
      return JSON.parse(localStorage.getItem('userData') || '{}')
    },
    filteredVsls() {
      if (!this.vslSearch.trim()) return this.vsls
      const q = this.vslSearch.toLowerCase()
      return this.vsls.filter(v => v.vsl_name && v.vsl_name.toLowerCase().includes(q))
    },
    selectedModule() {
      if (!this.selectedModuleId) return null
      return this.modules.find(m => m.id === this.selectedModuleId) || null
    },
    studentLinkTemplate() {
      const origin = (typeof window !== 'undefined' ? window.location.origin : '')
      return `${origin}/academia/${this.userData.id}/{CHAVE}`
    },
  },
  watch: {
    selectedModuleId(newId) {
      const m = this.modules.find(mod => mod.id === newId)
      if (m) {
        this.moduleEditForm = {
          title: m.title || '',
          description: m.description || '',
          sort_order: m.sort_order || 0,
          status: m.status || 'draft',
        }
      }
      this.lessonEditId = null
      this.addingLesson = false
    },
  },
  async mounted() {
    const PLATFORM_OWNER_EMAIL = 'edvomberto@gmail.com'
    try {
      const u = JSON.parse(localStorage.getItem('userData') || '{}')
      if ((u.user_email || u.email || '').toLowerCase() !== PLATFORM_OWNER_EMAIL) {
        this.$router.replace({ name: 'minha-academia' })
        return
      }
    } catch (_) {
      this.$router.replace({ name: 'minha-academia' })
      return
    }
    await Promise.allSettled([this.loadVsls(), this.loadModules()])
    this._vslClickOutside = () => { this.vslDropdownOpen = '' }
    document.addEventListener('click', this._vslClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this._vslClickOutside)
  },
  methods: {
    async loadVsls() {
      try {
        const { data } = await apiBack.get(`vslUserId/${this.userData.id}`)
        this.vsls = Array.isArray(data) ? data.filter(v => Number(v.vsl_ativo) === 1) : []
      } catch (_) {
        this.vsls = []
      }
    },
    vslNameById(id) {
      if (!id && id !== 0) return 'Selecione o vídeo'
      const v = this.vsls.find(v => String(v.id) === String(id))
      return v ? v.vsl_name : 'Selecione o vídeo'
    },
    toggleVslDropdown(key) {
      if (this.vslDropdownOpen === key) {
        this.vslDropdownOpen = ''
      } else {
        this.vslDropdownOpen = key
        this.vslSearch = ''
        this.$nextTick(() => {
          const ref = this.$refs['vslSearch_' + key]
          if (ref) ref.focus()
        })
      }
    },
    selectVsl(key, id) {
      if (key === 'edit') this.lessonEditForm.id_vsl = id
      else this.newLessonForm.id_vsl = id
      this.vslDropdownOpen = ''
      this.vslSearch = ''
    },
    async loadModules() {
      this.isLoadingModules = true
      this.actionError = ''
      try {
        const fetched = await this.fetchModulesWithFallback()
        this.modules = fetched
        if (!this.selectedModuleId && fetched.length) {
          this.selectModule(fetched[0].id)
        } else if (this.selectedModuleId) {
          const m = fetched.find(mod => mod.id === this.selectedModuleId)
          if (m) {
            this.moduleEditForm = {
              title: m.title || '',
              description: m.description || '',
              sort_order: m.sort_order || 0,
              status: m.status || 'draft',
            }
          }
        }
      } catch (err) {
        this.actionError = this.extractApiError(err, 'Erro ao carregar módulos')
      } finally {
        this.isLoadingModules = false
      }
    },
    normalizeTrainingModules(payload) {
      const rows = Array.isArray(payload) ? payload : ((payload && payload.modules) || [])
      return rows.map((m, modIdx) => ({
        id: Number(m.id),
        title: m.title || `Módulo ${modIdx + 1}`,
        description: m.description || '',
        sort_order: Number(m.sort_order) || modIdx,
        status: m.status || 'draft',
        lessons: (Array.isArray(m.lessons) ? m.lessons : []).map((l, lessonIdx) => ({
          id: Number(l.id),
          module_id: Number(l.module_id || m.id),
          id_vsl: l.id_vsl || '',
          title: l.title || `Aula ${lessonIdx + 1}`,
          description: l.description || '',
          sort_order: Number(l.sort_order) || lessonIdx,
          lesson_type: l.lesson_type || 'video',
          duration_min: l.duration_min || null,
          status: l.status || 'draft',
        })),
      }))
    },
    normalizeLegacyModules(modulosPayload, conteudosPayload) {
      const modulos = Array.isArray(modulosPayload) ? modulosPayload : []
      const conteudos = Array.isArray(conteudosPayload) ? conteudosPayload : []
      return modulos
        .map((m, modIdx) => ({
          id: Number(m.id),
          title: m.modulo_name || `Módulo ${modIdx + 1}`,
          description: '',
          sort_order: Number(m.modulo_ordem) || modIdx,
          status: 'draft',
          lessons: conteudos
            .filter(c => String(c.modulo_id) === String(m.id))
            .sort((a, b) => (Number(a.conteudo_sequencia) || 0) - (Number(b.conteudo_sequencia) || 0))
            .map((c, lessonIdx) => ({
              id: Number(c.id),
              module_id: Number(m.id),
              id_vsl: c.id_vsl || '',
              title: c.conteudo_titulo || `Aula ${lessonIdx + 1}`,
              description: c.conteudo_content || '',
              sort_order: Number(c.conteudo_sequencia) || lessonIdx,
              lesson_type: 'video',
              duration_min: null,
              status: 'draft',
            })),
        }))
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
    },
    async fetchModulesWithFallback() {
      if (this.apiMode !== 'legacy') {
        try {
          const { data } = await apiBack.get(`training/modules/${this.userData.id}`)
          this.apiMode = 'training'
          return this.normalizeTrainingModules(data)
        } catch (_) {}
      }

      const [modulosRes, conteudosRes] = await Promise.all([
        apiBack.get('getModulo'),
        apiBack.get('getConteudo'),
      ])
      this.apiMode = 'legacy'
      return this.normalizeLegacyModules(modulosRes.data, conteudosRes.data)
    },

    selectModule(id) {
      this.selectedModuleId = id
    },

    async createModule() {
      this.actionError = ''
      const order = this.modules.length
      const moduleTitle = `Módulo ${order + 1}`
      try {
        let insertedId = null
        if (this.apiMode !== 'legacy') {
          try {
            const { data } = await apiBack.post('training/modules', {
              id_user: this.userData.id,
              title: moduleTitle,
              description: '',
              sort_order: order,
              status: 'draft',
              is_active: 1,
            })
            this.apiMode = 'training'
            insertedId = data && data.id
          } catch (_) {
            this.apiMode = 'legacy'
          }
        }

        if (!insertedId) {
          await apiBack.post('createModulo', {
            modulo_name: moduleTitle,
            modulo_ativo: 1,
            modulo_ordem: order,
          })
        }

        await this.loadModules()
        if (insertedId) {
          this.selectModule(Number(insertedId))
        } else if (this.modules.length) {
          this.selectModule(this.modules[this.modules.length - 1].id)
        }
      } catch (err) {
        this.actionError = this.extractApiError(err, 'Erro ao criar módulo')
      }
    },

    async saveModule() {
      if (!this.selectedModule) return
      this.isSavingModule = true
      this.actionError = ''
      try {
        if (this.apiMode === 'legacy') {
          await apiBack.put(`updateModulo/${this.selectedModule.id}`, {
            modulo_name: this.moduleEditForm.title || 'Sem título',
            modulo_ativo: 1,
            modulo_ordem: Number(this.moduleEditForm.sort_order) || 0,
          })
        } else {
          await apiBack.put(`training/modules/${this.selectedModule.id}`, {
            ...this.moduleEditForm,
            is_active: 1,
          })
        }
        const idx = this.modules.findIndex(m => m.id === this.selectedModule.id)
        if (idx >= 0) {
          this.$set(this.modules, idx, { ...this.modules[idx], ...this.moduleEditForm })
        }
        this.$message({ type: 'success', message: 'Módulo salvo' })
      } catch (err) {
        this.actionError = this.extractApiError(err, 'Erro ao salvar módulo')
      } finally {
        this.isSavingModule = false
      }
    },

    confirmDeleteModule(id) {
      if (!window.confirm('Excluir este módulo e todas as suas aulas?')) return
      this.deleteModule(id)
    },
    async deleteModule(id) {
      try {
        if (this.apiMode === 'legacy') {
          await apiBack.delete(`deleteModulo/${id}`)
        } else {
          await apiBack.delete(`training/modules/${id}`)
        }
        await this.loadModules()
        if (this.selectedModuleId === id) {
          this.selectedModuleId = this.modules.length ? this.modules[0].id : null
        }
        this.$message({ type: 'success', message: 'Módulo excluído' })
      } catch (err) {
        this.actionError = this.extractApiError(err, 'Erro ao excluir módulo')
      }
    },

    startLessonEdit(lesson) {
      this.lessonEditId = lesson.id
      this.lessonEditForm = {
        title: lesson.title || '',
        description: lesson.description || '',
        id_vsl: lesson.id_vsl || '',
        lesson_type: lesson.lesson_type || 'video',
        duration_min: lesson.duration_min || null,
        status: lesson.status || 'draft',
        sort_order: lesson.sort_order || 0,
      }
    },
    cancelLessonEdit() {
      this.lessonEditId = null
    },
    async saveLesson(lesson) {
      this.isSavingLesson = true
      this.actionError = ''
      try {
        if (this.apiMode === 'legacy') {
          await apiBack.put(`updateConteudo/${lesson.id}`, {
            conteudo_titulo: this.lessonEditForm.title || 'Sem título',
            modulo_id: this.selectedModule.id,
            conteudo_content: this.lessonEditForm.description || '',
            conteudo_files: '',
            conteudo_video_url: '',
            conteudo_sequencia: Number(this.lessonEditForm.sort_order) || 0,
            status_video: this.lessonEditForm.status || 'draft',
            id_vsl: this.lessonEditForm.id_vsl || '',
          })
        } else {
          await apiBack.put(`training/lessons/${lesson.id}`, {
            ...this.lessonEditForm,
            is_active: 1,
          })
        }
        const mod = this.modules.find(m => m.id === this.selectedModuleId)
        if (mod) {
          const lIdx = mod.lessons.findIndex(l => l.id === lesson.id)
          if (lIdx >= 0) {
            this.$set(mod.lessons, lIdx, { ...mod.lessons[lIdx], ...this.lessonEditForm })
          }
        }
        this.lessonEditId = null
        this.$message({ type: 'success', message: 'Aula salva' })
      } catch (err) {
        this.actionError = this.extractApiError(err, 'Erro ao salvar aula')
      } finally {
        this.isSavingLesson = false
      }
    },

    async createLesson() {
      if (!this.newLessonForm.title.trim() || !this.selectedModule) return
      this.isSavingLesson = true
      this.actionError = ''
      const order = this.selectedModule.lessons ? this.selectedModule.lessons.length : 0
      try {
        if (this.apiMode === 'legacy') {
          await apiBack.post('createConteudo', {
            conteudo_titulo: this.newLessonForm.title.trim(),
            modulo_id: this.selectedModule.id,
            conteudo_content: this.newLessonForm.description || '',
            conteudo_files: '',
            conteudo_video_url: '',
            conteudo_sequencia: order,
            uid: `${Date.now()}${Math.floor(Math.random() * 1000)}`,
            id_vsl: this.newLessonForm.id_vsl || '',
          })
        } else {
          await apiBack.post('training/lessons', {
            module_id: this.selectedModule.id,
            id_user: this.userData.id,
            title: this.newLessonForm.title.trim(),
            description: this.newLessonForm.description || '',
            id_vsl: this.newLessonForm.id_vsl || '',
            lesson_type: this.newLessonForm.lesson_type || 'video',
            duration_min: this.newLessonForm.duration_min || null,
            status: 'draft',
            sort_order: order,
            is_active: 1,
          })
        }
        this.newLessonForm = { title: '', description: '', id_vsl: '', lesson_type: 'video', duration_min: null }
        this.addingLesson = false
        await this.loadModules()
        this.$message({ type: 'success', message: 'Aula adicionada' })
      } catch (err) {
        this.actionError = this.extractApiError(err, 'Erro ao criar aula')
      } finally {
        this.isSavingLesson = false
      }
    },

    confirmDeleteLesson(id) {
      if (!window.confirm('Excluir esta aula?')) return
      this.deleteLesson(id)
    },
    async deleteLesson(id) {
      try {
        if (this.apiMode === 'legacy') {
          await apiBack.delete(`deleteConteudo/${id}`)
        } else {
          await apiBack.delete(`training/lessons/${id}`)
        }
        this.lessonEditId = null
        await this.loadModules()
        this.$message({ type: 'success', message: 'Aula excluída' })
      } catch (err) {
        this.actionError = this.extractApiError(err, 'Erro ao excluir aula')
      }
    },

    /* ─── Drag: modules ─── */
    onModuleDragStart(e, idx) {
      this.moduleDragFromIdx = idx
      e.dataTransfer.effectAllowed = 'move'
    },
    onModuleDragOver(e, toIdx) {
      if (this.moduleDragFromIdx === null || this.moduleDragFromIdx === toIdx) return
      const items = [...this.modules]
      const [moved] = items.splice(this.moduleDragFromIdx, 1)
      items.splice(toIdx, 0, moved)
      this.modules = items
      this.moduleDragFromIdx = toIdx
    },
    async onModuleDragEnd() {
      this.moduleDragFromIdx = null
      try {
        if (this.apiMode === 'legacy') {
          await Promise.all(
            this.modules.map((m, idx) =>
              apiBack.put(`updateModulo/${m.id}`, {
                modulo_name: m.title,
                modulo_ativo: 1,
                modulo_ordem: idx,
              })
            )
          )
        } else {
          await Promise.all(
            this.modules.map((m, idx) =>
              apiBack.put(`training/modules/${m.id}`, {
                title: m.title,
                description: m.description || '',
                sort_order: idx,
                status: m.status || 'draft',
                is_active: 1,
              })
            )
          )
        }
      } catch (_) {}
    },

    /* ─── Drag: lessons ─── */
    onLessonDragStart(e, idx) {
      this.lessonDragFromIdx = idx
      e.dataTransfer.effectAllowed = 'move'
    },
    onLessonDragOver(e, toIdx) {
      if (this.lessonDragFromIdx === null || this.lessonDragFromIdx === toIdx) return
      const mod = this.selectedModule
      if (!mod) return
      const items = [...mod.lessons]
      const [moved] = items.splice(this.lessonDragFromIdx, 1)
      items.splice(toIdx, 0, moved)
      const modIdx = this.modules.findIndex(m => m.id === mod.id)
      if (modIdx >= 0) this.$set(this.modules, modIdx, { ...mod, lessons: items })
      this.lessonDragFromIdx = toIdx
    },
    async onLessonDragEnd() {
      this.lessonDragFromIdx = null
      if (!this.selectedModule) return
      try {
        if (this.apiMode === 'legacy') {
          await Promise.all(
            this.selectedModule.lessons.map((l, idx) =>
              apiBack.put(`updateConteudo/${l.id}`, {
                conteudo_titulo: l.title,
                modulo_id: this.selectedModule.id,
                conteudo_content: l.description || '',
                conteudo_files: '',
                conteudo_video_url: '',
                conteudo_sequencia: idx,
                status_video: l.status || 'draft',
                id_vsl: l.id_vsl || '',
              })
            )
          )
        } else {
          await Promise.all(
            this.selectedModule.lessons.map((l, idx) =>
              apiBack.put(`training/lessons/${l.id}`, {
                title: l.title,
                description: l.description || '',
                id_vsl: l.id_vsl || '',
                sort_order: idx,
                status: l.status || 'draft',
                lesson_type: l.lesson_type || 'video',
                duration_min: l.duration_min || null,
                is_active: 1,
              })
            )
          )
        }
      } catch (_) {}
    },

    /* ─── Helpers ─── */
    statusLabel(status) {
      return { draft: 'Rascunho', editing: 'Editando', published: 'Publicado' }[status] || 'Rascunho'
    },
    statusClass(status) {
      return { draft: 'am-badge--amber', editing: 'am-badge--blue', published: 'am-badge--green' }[status] || 'am-badge--amber'
    },
    lessonTypeIcon(type) {
      return { video: '▶', text: '📄', exercise: '✎' }[type] || '▶'
    },
    lessonTypeIconClass(type) {
      return { video: 'am-type-icon--video', text: 'am-type-icon--text', exercise: 'am-type-icon--exercise' }[type] || 'am-type-icon--video'
    },
    lessonTypeLabel(type) {
      return { video: 'Vídeo', text: 'Texto', exercise: 'Exercício' }[type] || 'Vídeo'
    },
    moduleTotalDuration(m) {
      if (!m.lessons || !m.lessons.length) return ''
      const total = m.lessons.reduce((acc, l) => acc + (Number(l.duration_min) || 0), 0)
      if (!total) return ''
      if (total < 60) return `${total}min`
      const h = Math.floor(total / 60), min = total % 60
      return min ? `${h}h ${min}min` : `${h}h`
    },
    moduleIndexLabel(lessonIdx) {
      const modIdx = this.modules.findIndex(m => m.id === this.selectedModuleId)
      return `${modIdx >= 0 ? modIdx + 1 : 1}.${lessonIdx + 1}`
    },
    previewCourse() {
      window.open(`${window.location.origin}/academia/${this.userData.id}/preview`, '_blank')
    },
    async switchToAlunos() {
      this.activeTab = 'alunos'
      if (!this.monitoring.students.length) await this.loadMonitoring()
    },
    async loadMonitoring() {
      this.monitoringLoading = true
      try {
        const { data } = await apiBack.get(`training/monitoring/${this.userData.id}`)
        this.monitoring = data || { students: [], total_lessons: 0, lesson_ratings: [] }
      } catch (e) {
        console.error('[AcademyManager] loadMonitoring', e)
      } finally {
        this.monitoringLoading = false
      }
    },
    copyStudentLink() {
      const url = `${window.location.origin}/academia/${this.userData.id}/{CHAVE_DO_ALUNO}`
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => {
          this.linkCopied = true
          setTimeout(() => { this.linkCopied = false }, 2500)
        })
      }
    },
    formatDate(dt) {
      if (!dt) return '-'
      try {
        return new Date(dt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      } catch { return '-' }
    },
    extractApiError(err, fallback) {
      const status = err && err.response && err.response.status
      const msg = err && err.response && err.response.data && (err.response.data.error || err.response.data.message)
      if (status && msg) return `${fallback} (${status}: ${msg})`
      if (status) return `${fallback} (${status})`
      return fallback
    },
  },
}
</script>

<style scoped>
.am-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  --am-border: rgba(255,255,255,0.09);
  --am-bg-card: rgba(255,255,255,0.04);
  --am-bg-hover: rgba(255,255,255,0.06);
  --am-text: #e2e8f0;
  --am-text-muted: rgba(255,255,255,0.4);
  --am-text-secondary: rgba(255,255,255,0.65);
  --am-input-bg: rgba(255,255,255,0.06);
  --am-input-border: rgba(255,255,255,0.12);
  --am-accent: #3b82f6;
}

/* Top bar */
.am-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0 12px;
  border-bottom: 1px solid var(--am-border);
  flex-shrink: 0;
}
.am-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 13px; }
.am-breadcrumb__item { color: var(--am-text-secondary); }
.am-breadcrumb__item--link { color: var(--am-accent); cursor: pointer; }
.am-breadcrumb__sep { color: var(--am-text-muted); }
.am-topbar__right { display: flex; gap: 8px; }

.am-alert--error {
  background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.3);
  color: #fca5a5;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  margin: 8px 0 0;
  flex-shrink: 0;
}

/* Two-panel layout */
.am-panels {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

/* Left panel */
.am-left {
  width: 360px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--am-border);
  overflow: hidden;
}
.am-left__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px 10px;
  flex-shrink: 0;
}
.am-left__title { color: var(--am-text); font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; }
.am-left__count {
  background: rgba(255,255,255,0.1);
  color: var(--am-text-secondary);
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 700;
}
.am-module-list { flex: 1; overflow-y: auto; padding: 0 8px; }

.am-module-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  margin-bottom: 3px;
  transition: background .12s;
}
.am-module-item:hover { background: var(--am-bg-hover); }
.am-module-item--active { background: rgba(59,130,246,0.08); border-color: rgba(59,130,246,0.4); }
.am-drag-handle {
  color: var(--am-text-muted);
  font-size: 12px;
  cursor: grab;
  flex-shrink: 0;
  letter-spacing: -2px;
  user-select: none;
}
.am-module-item__body { flex: 1; min-width: 0; }
.am-module-item__title {
  color: var(--am-text);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.am-module-item__num { color: var(--am-text-muted); margin-right: 3px; }
.am-module-item__meta { color: var(--am-text-muted); font-size: 11px; margin-top: 2px; }

.am-btn-add-module {
  margin: 8px 8px 12px;
  background: rgba(255,255,255,0.04);
  border: 1px dashed rgba(255,255,255,0.14);
  border-radius: 10px;
  color: var(--am-text-secondary);
  font-size: 13px;
  font-weight: 600;
  padding: 10px;
  cursor: pointer;
  text-align: center;
  transition: background .12s;
  flex-shrink: 0;
}
.am-btn-add-module:hover { background: rgba(255,255,255,0.08); }

/* Right panel */
.am-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px 20px;
  gap: 0;
}

/* Section */
.am-section {
  background: var(--am-bg-card);
  border: 1px solid var(--am-border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
}
.am-section__title {
  margin: 0 0 14px;
  color: var(--am-text);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.am-section__count {
  background: rgba(255,255,255,0.1);
  color: var(--am-text-secondary);
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 700;
}

/* Form elements */
.am-form-grid { display: flex; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
.am-field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.am-field--flex1 { flex: 1; min-width: 80px; }
.am-field--flex2 { flex: 2; min-width: 120px; }
.am-field--flex3 { flex: 3; min-width: 160px; }
.am-label { color: var(--am-text-muted); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; }
.am-input, .am-textarea {
  background: var(--am-input-bg);
  border: 1px solid var(--am-input-border);
  border-radius: 8px;
  color: var(--am-text);
  font-size: 13px;
  padding: 8px 10px;
  width: 100%;
  box-sizing: border-box;
}
.am-input:focus, .am-textarea:focus { outline: none; border-color: rgba(59,130,246,0.5); }
.am-textarea { resize: vertical; }

/* Improve native select readability on dark theme */
select.am-input {
  background-color: #1a2030;
  color: #e9edf7;
}
select.am-input option {
  background-color: #1a2030;
  color: #e9edf7;
}
select.am-input option:disabled {
  color: rgba(233, 237, 247, 0.5);
}
.am-form-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }

/* Lesson items */
.am-lesson-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 8px;
  border-radius: 8px;
  border: 1px solid transparent;
  margin-bottom: 3px;
  transition: background .12s;
}
.am-lesson-item:hover { background: var(--am-bg-hover); }
.am-lesson-type-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}
.am-type-icon--video   { background: rgba(59,130,246,0.15);  color: #60a5fa; }
.am-type-icon--text    { background: rgba(255,255,255,0.08);  color: rgba(255,255,255,0.5); }
.am-type-icon--exercise { background: rgba(232,106,16,0.15); color: #fb923c; }
.am-lesson-item__body { flex: 1; min-width: 0; }
.am-lesson-item__title {
  color: var(--am-text);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.am-lesson-item__num { color: var(--am-text-muted); margin-right: 4px; }
.am-lesson-item__meta { color: var(--am-text-muted); font-size: 11px; margin-top: 2px; }

.am-lesson-edit-form {
  flex: 1;
  background: rgba(255,255,255,0.03);
  border: 1px solid var(--am-border);
  border-radius: 10px;
  padding: 12px;
}
.am-add-lesson-form {
  background: rgba(255,255,255,0.03);
  border: 1px dashed rgba(255,255,255,0.12);
  border-radius: 10px;
  padding: 12px;
  margin-top: 8px;
}
.am-btn-add-lesson {
  width: 100%;
  background: transparent;
  border: 1px dashed rgba(255,255,255,0.12);
  border-radius: 8px;
  color: var(--am-text-secondary);
  font-size: 13px;
  font-weight: 600;
  padding: 10px;
  cursor: pointer;
  text-align: center;
  margin-top: 8px;
  transition: background .12s;
}
.am-btn-add-lesson:hover { background: rgba(255,255,255,0.05); }

/* Badges */
.am-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: .05em;
}
.am-badge--amber { background: rgba(245,158,11,0.15); color: #fbbf24; }
.am-badge--blue  { background: rgba(59,130,246,0.15);  color: #60a5fa; }
.am-badge--green { background: rgba(16,185,129,0.15);  color: #34d399; }

/* Buttons */
.am-btn {
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
  white-space: nowrap;
}
.am-btn--primary { background: #E86A10; color: #fff; }
.am-btn--primary:hover { background: #d45e0a; }
.am-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.am-btn--ghost {
  background: rgba(255,255,255,0.07);
  color: var(--am-text-secondary);
  border: 1px solid rgba(255,255,255,0.09);
}
.am-btn--ghost:hover { background: rgba(255,255,255,0.12); }
.am-btn--danger-ghost {
  background: rgba(239,68,68,0.08);
  color: #f87171;
  border: 1px solid rgba(239,68,68,0.2);
}
.am-btn--danger-ghost:hover { background: rgba(239,68,68,0.15); }
.am-btn--sm { padding: 6px 10px; font-size: 12px; }

/* Empty */
.am-empty { color: var(--am-text-muted); font-size: 13px; }
.am-empty--pad { padding: 12px 16px; }
.am-empty--center { text-align: center; padding: 60px 20px; flex: 1; display: flex; align-items: center; justify-content: center; }

@media (max-width: 900px) {
  .am-panels { flex-direction: column; overflow: visible; }
  .am-left { width: 100%; border-right: none; border-bottom: 1px solid var(--am-border); max-height: 50vh; }
  .am-right { padding: 12px; }
}

/* ── Tabs ── */
.am-tabs {
  display: flex;
  gap: 2px;
  background: rgba(255,255,255,0.05);
  border-radius: 8px;
  padding: 3px;
}
.am-tab {
  padding: 6px 16px;
  border: none;
  background: transparent;
  color: var(--am-text-secondary);
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all .15s;
}
.am-tab--active { background: var(--am-bg-card); color: #fff; box-shadow: 0 1px 4px rgba(0,0,0,.2); }
.am-tab:not(.am-tab--active):hover { color: #fff; }

/* ── Monitoring ── */
.am-monitoring {
  padding: 20px;
  flex: 1;
  overflow: auto;
}
.am-monitoring__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}
.am-monitoring__title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}
.am-mon-table-wrap {
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid var(--am-border);
  margin-bottom: 24px;
}
.am-mon-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.am-mon-table thead tr {
  background: rgba(255,255,255,0.04);
  border-bottom: 1px solid var(--am-border);
}
.am-mon-table th {
  padding: 10px 14px;
  text-align: left;
  color: var(--am-text-muted);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .04em;
  white-space: nowrap;
}
.am-mon-table td {
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  color: var(--am-text-secondary);
  vertical-align: middle;
}
.am-mon-table tbody tr:last-child td { border-bottom: none; }
.am-mon-table tbody tr:hover td { background: rgba(255,255,255,0.02); }
.am-mon-student__name { display: block; font-weight: 600; color: #fff; }
.am-mon-student__email { display: block; font-size: 11px; color: var(--am-text-muted); margin-top: 2px; }
.am-mon-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(255,255,255,0.07);
  color: var(--am-text-secondary);
}
.am-mon-badge--done { background: rgba(16,185,129,0.15); color: #34d399; }
.am-mon-pct-wrap { display: flex; align-items: center; gap: 8px; }
.am-mon-pct-bar {
  width: 60px;
  height: 5px;
  background: rgba(255,255,255,0.1);
  border-radius: 99px;
  overflow: hidden;
  flex-shrink: 0;
}
.am-mon-pct-fill { height: 100%; background: #E86A10; border-radius: 99px; }
.am-mon-date { font-size: 11px; color: var(--am-text-muted); white-space: nowrap; }

/* Ratings per lesson */
.am-mon-ratings { margin-top: 4px; }
.am-mon-ratings__title { font-size: 14px; font-weight: 700; color: #fff; margin: 0 0 14px; }
.am-mon-ratings__list { display: flex; flex-direction: column; gap: 8px; }
.am-mon-rating-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  background: var(--am-bg-card);
  border: 1px solid var(--am-border);
  border-radius: 10px;
}
.am-mon-rating-info { min-width: 0; }
.am-mon-rating-module { display: block; font-size: 10px; color: var(--am-text-muted); text-transform: uppercase; letter-spacing: .04em; }
.am-mon-rating-lesson { display: block; font-size: 13px; font-weight: 600; color: #fff; }
.am-mon-stars { display: flex; align-items: center; gap: 3px; flex-shrink: 0; }
.am-mon-star { font-size: 18px; color: rgba(255,255,255,0.15); }
.am-mon-star--on { color: #F5B935; }
.am-mon-star-avg { font-size: 12px; color: var(--am-text-muted); margin-left: 8px; }

/* ── Student link card ── */
.am-student-link {
  flex-shrink: 0;
  margin: 12px 8px 8px;
  background: rgba(232,106,16,0.07);
  border: 1px solid rgba(232,106,16,0.2);
  border-radius: 10px;
  padding: 12px;
}
.am-student-link__header {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  color: #E86A10;
  text-transform: uppercase;
  letter-spacing: .05em;
  margin-bottom: 7px;
}
.am-student-link__desc {
  font-size: 11px;
  color: var(--am-text-muted);
  margin: 0 0 8px;
  line-height: 1.4;
}
.am-student-link__desc code {
  background: rgba(255,255,255,0.1);
  padding: 1px 5px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 10px;
  color: #F5B935;
}
.am-student-link__row {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 7px;
  padding: 7px 8px;
  margin-bottom: 8px;
}
.am-student-link__url {
  flex: 1;
  font-size: 10px;
  font-family: monospace;
  color: rgba(255,255,255,0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.am-student-link__copy {
  flex-shrink: 0;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 5px;
  padding: 4px 6px;
  cursor: pointer;
  color: rgba(255,255,255,0.6);
  display: flex;
  align-items: center;
  transition: background .15s;
}
.am-student-link__copy:hover { background: rgba(255,255,255,0.15); }
.am-student-link__preview {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: none;
  border: 1px solid rgba(232,106,16,0.3);
  border-radius: 7px;
  padding: 7px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #E86A10;
  cursor: pointer;
  transition: background .15s;
}
.am-student-link__preview:hover { background: rgba(232,106,16,0.1); }

/* ── VSL custom dropdown ── */
.am-vsl-dd {
  position: relative;
  width: 100%;
}
.am-vsl-dd__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  background: var(--am-input-bg);
  border: 1px solid var(--am-input-border);
  border-radius: 8px;
  color: var(--am-text);
  font-size: 13px;
  padding: 8px 10px;
  cursor: pointer;
  text-align: left;
  gap: 6px;
  transition: border-color .15s;
}
.am-vsl-dd__trigger:hover { border-color: rgba(255,255,255,0.22); }
.am-vsl-dd__label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--am-text);
}
.am-vsl-dd__panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 999;
  background: #1e2130;
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.55);
  overflow: hidden;
}
.am-vsl-dd__search-wrap {
  padding: 8px 8px 6px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.am-vsl-dd__search {
  width: 100%;
  box-sizing: border-box;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 6px;
  color: #e2e8f0;
  font-size: 12px;
  padding: 6px 10px;
  outline: none;
}
.am-vsl-dd__search::placeholder { color: rgba(255,255,255,0.3); }
.am-vsl-dd__search:focus { border-color: rgba(59,130,246,0.5); }
.am-vsl-dd__list {
  max-height: 220px;
  overflow-y: auto;
  padding: 4px 0;
}
.am-vsl-dd__item {
  padding: 8px 12px;
  font-size: 13px;
  color: rgba(255,255,255,0.7);
  cursor: pointer;
  transition: background .1s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.am-vsl-dd__item:hover { background: rgba(255,255,255,0.07); color: #fff; }
.am-vsl-dd__item--selected { color: #E86A10; background: rgba(232,106,16,0.1); }
.am-vsl-dd__empty {
  padding: 10px 12px;
  font-size: 12px;
  color: rgba(255,255,255,0.3);
  text-align: center;
}
</style>
